import { driver, type DriveStep } from "driver.js";
import "driver.js/dist/driver.css";
import { isShowMode } from "../showMode";

export type ShowTourName = "home" | "thermo" | "thickness";

export const SHOW_TOUR_REQUEST_EVENT = "zeroco2:show-tour-request";
export const SHOW_TOUR_SETTINGS_EVENT = "zeroco2:show-tour-settings";

const TOUR_VERSION = "v1";
const TOUR_READY_RETRY_MS = 500;
const TOUR_READY_MAX_ATTEMPTS = 20;
const TOUR_NAMES: ShowTourName[] = ["home", "thermo", "thickness"];
const TOUR_ENABLED_KEY = `zeroco2.showTour.enabled.${TOUR_VERSION}`;
let activeDriver: ReturnType<typeof driver> | null = null;
let suppressNextDestroyDone = false;

export function showTourKey(name: ShowTourName) {
  return `zeroco2.showTour.${name}.${TOUR_VERSION}`;
}

export function shouldAutoStartShowTour(name: ShowTourName) {
  if (!isShowMode || typeof window === "undefined") return false;
  if (!isShowTourEnabled()) return false;
  return window.localStorage.getItem(showTourKey(name)) !== "done";
}

export function markShowTourDone(name: ShowTourName) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(showTourKey(name), "done");
  if (allShowToursDone()) {
    setShowTourEnabled(false);
  }
}

export function isShowTourEnabled() {
  if (!isShowMode || typeof window === "undefined") return false;
  const stored = window.localStorage.getItem(TOUR_ENABLED_KEY);
  if (stored === "0") return false;
  if (stored === "1") return true;
  return !allShowToursDone();
}

export function setShowTourEnabled(enabled: boolean) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(TOUR_ENABLED_KEY, enabled ? "1" : "0");
  if (enabled) {
    TOUR_NAMES.forEach((name) => window.localStorage.removeItem(showTourKey(name)));
  }
  window.dispatchEvent(new CustomEvent(SHOW_TOUR_SETTINGS_EVENT, { detail: { enabled } }));
}

export function onShowTourSettingsChange(handler: (enabled: boolean) => void) {
  if (typeof window === "undefined") return () => {};
  const listener = () => handler(isShowTourEnabled());
  window.addEventListener(SHOW_TOUR_SETTINGS_EVENT, listener);
  window.addEventListener("storage", listener);
  return () => {
    window.removeEventListener(SHOW_TOUR_SETTINGS_EVENT, listener);
    window.removeEventListener("storage", listener);
  };
}

function allShowToursDone() {
  if (typeof window === "undefined") return false;
  return TOUR_NAMES.every((name) => window.localStorage.getItem(showTourKey(name)) === "done");
}

export function requestShowTour(name: ShowTourName) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(SHOW_TOUR_REQUEST_EVENT, { detail: { name } }));
}

export function requestShowTourForPath(pathname: string) {
  if (pathname.startsWith("/thermoimaging")) {
    requestShowTour("thermo");
    return;
  }
  if (pathname.startsWith("/thickness")) {
    requestShowTour("thickness");
    return;
  }
  requestShowTour("home");
}

export function onShowTourRequest(name: ShowTourName, handler: () => void) {
  if (typeof window === "undefined") return () => {};
  const listener = (event: Event) => {
    const requested = (event as CustomEvent<{ name?: ShowTourName }>).detail?.name;
    if (requested === name) handler();
  };
  window.addEventListener(SHOW_TOUR_REQUEST_EVENT, listener);
  return () => window.removeEventListener(SHOW_TOUR_REQUEST_EVENT, listener);
}

function canStartTourNow() {
  if (typeof document === "undefined") return false;
  if (document.querySelector(".analytics-consent")) return false;
  if (document.querySelector(".loading-popup")) return false;
  return true;
}

export function scheduleShowTourStart(start: () => void) {
  if (typeof window === "undefined") return () => {};
  let attempts = 0;
  let timer: number | null = null;
  let cancelled = false;

  const tryStart = () => {
    if (cancelled) return;
    attempts += 1;
    if (canStartTourNow()) {
      start();
      return;
    }
    if (attempts >= TOUR_READY_MAX_ATTEMPTS) return;
    timer = window.setTimeout(tryStart, TOUR_READY_RETRY_MS);
  };

  timer = window.setTimeout(tryStart, TOUR_READY_RETRY_MS);
  return () => {
    cancelled = true;
    if (timer != null) window.clearTimeout(timer);
  };
}

export function tourElement(id: string) {
  return `[data-tour-id="${id}"]`;
}

function resolveSteps(steps: DriveStep[]) {
  const modals = Array.from(document.querySelectorAll<HTMLElement>(".modal-backdrop .modal"));
  const activeModal = modals.length ? modals[modals.length - 1] : null;
  return steps.filter((step) => {
    if (!step.element || typeof step.element !== "string") return true;
    const element = document.querySelector(step.element);
    if (!element) return false;
    if (activeModal && !activeModal.contains(element)) return false;
    return true;
  });
}

type StartShowTourOptions = {
  markDone?: boolean;
  onDestroyed?: () => void;
};

export function dismissActiveShowTour(options: { markDone?: boolean } = {}) {
  if (!activeDriver) return;
  suppressNextDestroyDone = options.markDone !== true;
  activeDriver.destroy();
}

export function startShowTour(
  name: ShowTourName,
  steps: DriveStep[],
  options: StartShowTourOptions = {}
) {
  if (!isShowMode || typeof document === "undefined") return false;
  if (!canStartTourNow()) return false;

  const resolvedSteps = resolveSteps(steps);
  if (!resolvedSteps.length) return false;

  dismissActiveShowTour({ markDone: false });

  const instance = driver({
    steps: resolvedSteps,
    animate: true,
    smoothScroll: true,
    allowClose: true,
    overlayClickBehavior: "close",
    overlayOpacity: 0.62,
    stagePadding: 8,
    stageRadius: 12,
    disableActiveInteraction: true,
    popoverClass: "show-tour-popover",
    showButtons: ["next", "previous", "close"],
    showProgress: true,
    nextBtnText: "Next",
    prevBtnText: "Back",
    doneBtnText: "Done",
    onDestroyed: () => {
      const suppressDone = activeDriver === instance && suppressNextDestroyDone;
      if (activeDriver === instance) {
        activeDriver = null;
        suppressNextDestroyDone = false;
      }
      if (!suppressDone && options.markDone !== false) markShowTourDone(name);
      options.onDestroyed?.();
    }
  });

  activeDriver = instance;
  suppressNextDestroyDone = false;
  instance.drive();
  return true;
}
